/**
 * frontend/src/pages/valuation/_FinancialsTyping.tsx
 *
 * VAL-PATH-TYPED-FINANCIALS (P2c) — step 2's second route: the owner types each
 * year's figures (the same seven the upload fills). The save is the existing
 * PUT .../financials, which replaces every row, so the years read from files are
 * sent back unchanged first and the typed years after them. A year that came
 * from a file is not typed here. Nothing is computed in the browser.
 * The screen reads what is typed through `typed` (one save behind both the
 * "Save figures" button and the step footer's "Save and continue").
 */
import { useEffect, useRef, useState, type MutableRefObject } from 'react'

import type { FinancialsRow, OwnerEnteredCell } from '@/api/valuationFinancials'
import { FIELDS } from './_FinancialsYearTable'
import { HelpDot } from '@/components/ui/help-dot'
import { HELP_TEXTS, type HelpKey } from './_helpTexts'
import { CLIENT_TEXT } from './_typedFinancialsText'

type Entry = { id: number; year: string; values: Record<string, string> }
/** What is typed: the rows to send (and whether they differ from what was loaded), or why they cannot be sent. */
export type TypedFigures = { rows: FinancialsRow[]; changed: boolean } | { invalid: string }

const BTN = 'rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50'
const PRIMARY = `${BTN} bg-blue-700 text-pal-white hover:bg-blue-800`
const SECONDARY = `${BTN} border border-pal-line-strong bg-pal-white text-gray-800 hover:bg-pal-surface-subtle`

/** Lives here, not in the API module: tests mock that module wholesale. */
export const isOwnerEntered = (p: unknown): p is OwnerEnteredCell =>
  typeof p === 'object' && p !== null && (p as { status?: unknown }).status === 'owner_entered'

/** Years with at least one figure read from a file. */
export function uploadedYears(provenance: Record<string, unknown>): Set<number> {
  const out = new Set<number>()
  for (const [k, p] of Object.entries(provenance)) {
    if (isOwnerEntered(p)) continue
    if (typeof p === 'object' && p !== null && (p as { filename?: unknown }).filename) out.add(Number(k.split('.')[0]))
  }
  return out
}

/** True when any figure on file was typed by the owner. */
export function hasTypedFigures(provenance: Record<string, unknown>): boolean {
  return Object.values(provenance).some(isOwnerEntered)
}

function typedRows(entries: Entry[]): FinancialsRow[] {
  return entries.map(e => {
    const row: FinancialsRow = { year: Number(e.year) }
    for (const f of FIELDS) {
      const v = (e.values[f.key] ?? '').trim()
      if (v !== '') row[f.key] = Number(v)
    }
    return row
  })
}

export function FinancialsTyping({ rows, provenance, busy, typed, onSave }: {
  rows: FinancialsRow[]
  provenance: Record<string, unknown>
  busy: boolean
  typed: MutableRefObject<(() => TypedFigures) | null>
  onSave: () => void
}) {
  const fromFiles = uploadedYears(provenance)
  const nextId = useRef(0)
  const [entries, setEntries] = useState<Entry[]>(() =>
    rows.filter(r => !fromFiles.has(r.year)).map(r => ({
      id: nextId.current++,
      year: String(r.year),
      values: Object.fromEntries(FIELDS.flatMap(f => (typeof r[f.key] === 'number' ? [[f.key, String(r[f.key])]] : []))),
    })))
  const [loaded] = useState(() => JSON.stringify(typedRows(entries)))

  /** A new year starts one below the earliest year on the screen, so its name is never blank. */
  function addYear() {
    const taken = new Set([...fromFiles, ...entries.map(e => Number(e.year)).filter(Number.isFinite)])
    let y = taken.size ? Math.min(...taken) - 1 : new Date().getFullYear() - 1
    while (taken.has(y)) y -= 1
    setEntries(es => [...es, { id: nextId.current++, year: String(y), values: {} }])
  }

  const update = (id: number, change: Partial<Entry>) =>
    setEntries(es => es.map(e => (e.id === id ? { ...e, ...change } : e)))

  function read(): TypedFigures {
    const seen = new Set<number>()
    for (const e of entries) {
      const y = Number(e.year)
      if (!/^\d{4}$/.test(e.year.trim())) return { invalid: CLIENT_TEXT.YEAR_MISSING }
      if (fromFiles.has(y)) return { invalid: CLIENT_TEXT.YEAR_FROM_FILES(y) }
      if (seen.has(y)) return { invalid: CLIENT_TEXT.YEAR_TWICE(y) }
      seen.add(y)
    }
    const mine = typedRows(entries)
    return { rows: [...rows.filter(r => fromFiles.has(r.year)), ...mine], changed: JSON.stringify(mine) !== loaded }
  }

  // The screen's save reads the latest entries; gone when this route is not shown.
  typed.current = read
  useEffect(() => () => { typed.current = null }, [typed])

  return (
    <section aria-label={CLIENT_TEXT.ROUTE_TYPE} className="space-y-4">
      <p className="text-sm text-pal-ink-body">{CLIENT_TEXT.TYPE_INTRO}</p>
      {entries.map(e => (
        <div key={e.id} role="group" aria-label={`FY${e.year}`} className="space-y-3 rounded-lg border border-pal-line p-4">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div className="block text-sm text-gray-800">
              <div className="flex items-center gap-1">
                <label htmlFor={`fy-year-${e.id}`}>{CLIENT_TEXT.YEAR_LABEL}</label>
                <HelpDot label={CLIENT_TEXT.YEAR_LABEL} paragraphs={HELP_TEXTS['fy-year']} testId="info-fy-year" />
              </div>
              <input id={`fy-year-${e.id}`} type="text" inputMode="numeric" maxLength={4} value={e.year}
                className="mt-1 block w-24 rounded border border-pal-line-strong p-1 text-sm"
                onChange={ev => update(e.id, { year: ev.target.value })} />
            </div>
            <button type="button" className={SECONDARY} disabled={busy}
              onClick={() => setEntries(es => es.filter(x => x.id !== e.id))}>{CLIENT_TEXT.REMOVE_YEAR(e.year)}</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map(f => (
              <div key={f.key} className="block text-sm text-gray-800">
                <div className="flex items-center gap-1">
                  <span aria-hidden="true">{f.label}</span>
                  <HelpDot label={f.label} paragraphs={HELP_TEXTS[`fy-${f.key}` as HelpKey]} testId={`info-fy-${f.key}`} />
                </div>
                <input type="number" aria-label={`${f.label} FY${e.year}`} value={e.values[f.key] ?? ''}
                  className="mt-1 block w-full rounded border border-pal-line-strong p-1 text-sm"
                  onChange={ev => update(e.id, { values: { ...e.values, [f.key]: ev.target.value } })} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button type="button" className={SECONDARY} disabled={busy} onClick={addYear}>{CLIENT_TEXT.ADD_YEAR}</button>
        <button type="button" className={PRIMARY} disabled={busy || entries.length === 0} onClick={onSave}>{CLIENT_TEXT.SAVE}</button>
      </div>
    </section>
  )
}
